<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  visit: { type: Object, default: () => ({}) },
  reason: { type: String, default: '' },
  isLoading: { type: Boolean, default: false },
  errorMessage: { type: String, default: '' },
});

const emit = defineEmits(['configure', 'keep', 'retry']);

const imovel = computed(() => props.visit?.imovel || {});
const title = computed(() => imovel.value?.titulo || 'Imóvel da visita');
const address = computed(() =>
  [imovel.value?.endereco, imovel.value?.bairro, imovel.value?.cidade]
    .filter(Boolean)
    .join(', ')
);
const media = computed(() => {
  const list = imovel.value?.midias || imovel.value?.media || [];
  const first = Array.isArray(list) ? list[0] : null;
  if (typeof first === 'string') return first;
  return first?.url || first?.src || first?.image_url || '';
});

const reasonText = computed(() => {
  const map = {
    user_conflict: 'O corretor já possui outro compromisso neste horário.',
    contact_conflict: 'O cliente já possui outro compromisso neste horário.',
    imovel_conflict: 'Este horário já está ocupado por outra visita.',
    outside_business_hours: 'O horário fica fora da janela de atendimento.',
    no_slots: 'Não há horários disponíveis para antecipar esta visita.',
  };

  return map[props.reason] || 'Este horário já está ocupado.';
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="grid gap-4 rounded-lais border border-orange-200 bg-orange-50 px-4 py-4"
  >
    <div class="flex items-start gap-3">
      <span
        class="i-lucide-calendar-clock mt-0.5 size-5 shrink-0 text-orange-700"
        aria-hidden="true"
      />
      <div class="min-w-0">
        <h3 class="text-[15px] font-semibold text-lais-text">
          Antecipação indisponível
        </h3>
        <p class="mt-1 text-[13px] leading-relaxed text-lais-text-body">
          {{ reasonText }} A visita atual continua agendada.
        </p>
      </div>
    </div>

    <div
      v-if="isLoading"
      class="grid min-h-[96px] place-items-center rounded-lais-sm border border-orange-200 bg-white/70 text-[13px] text-lais-text-muted"
    >
      <span class="inline-flex items-center gap-2">
        <span
          class="i-lucide-loader-circle size-4 animate-spin"
          aria-hidden="true"
        />
        Carregando horários…
      </span>
    </div>

    <div
      v-else
      class="flex gap-3 rounded-lais-sm border border-orange-200 bg-white px-3 py-3"
    >
      <div
        class="grid size-16 shrink-0 place-items-center overflow-hidden rounded-lais-sm bg-lais-bg-soft"
      >
        <img
          v-if="media"
          :src="media"
          :alt="title"
          class="size-full object-cover"
        />
        <span
          v-else
          class="i-lucide-building-2 size-6 text-lais-text-empty"
          aria-hidden="true"
        />
      </div>
      <div class="min-w-0">
        <p class="truncate text-[14px] font-semibold text-lais-text">
          {{ title }}
        </p>
        <p class="mt-1 line-clamp-2 text-[12px] text-lais-text-muted">
          {{ address || 'Endereço não informado' }}
        </p>
      </div>
    </div>

    <p
      v-if="errorMessage"
      class="rounded-lais-sm border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700"
    >
      {{ errorMessage }}
    </p>

    <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
      <LaisPrimaryButton variant="ghost" @click="emit('keep')">
        Manter visita atual
      </LaisPrimaryButton>
      <LaisPrimaryButton
        v-if="errorMessage"
        variant="ghost"
        icon="i-lucide-refresh-cw"
        @click="emit('retry')"
      >
        Tentar novamente
      </LaisPrimaryButton>
      <LaisPrimaryButton
        icon="i-lucide-settings"
        :is-loading="isLoading"
        @click="emit('configure')"
      >
        Configurar novos horários
      </LaisPrimaryButton>
    </div>
  </section>
</template>
